/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useMemo } from 'react';
import { getIsDateTime, getIsDate, getIsTime } from '../utils/typeGuards.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
const defaultReturnFalse = () => false;

export const useGetPropsBasedOnType = (propsWithDefaults: DSControlledDateTimePickerT.InternalProps) => {
  const isDateTime = getIsDateTime(propsWithDefaults);
  const isDate = getIsDate(propsWithDefaults);
  const isTime = getIsTime(propsWithDefaults);

  /* -------------------------- -------------- --------------------------*/
  /* -------------------------- DATE OPTIONALS --------------------------*/
  /* -------------------------- -------------- --------------------------*/
  const getIsDisabledDay =
    (isDateTime || isDate) && propsWithDefaults.getIsDisabledDay
      ? propsWithDefaults.getIsDisabledDay
      : defaultReturnFalse;
  const getIsOutOfRangeDay =
    (isDateTime || isDate) && propsWithDefaults.getIsOutOfRangeDay
      ? propsWithDefaults.getIsOutOfRangeDay
      : defaultReturnFalse;
  const appOnMonthChange =
    (isDateTime || isDate) && propsWithDefaults.onMonthChange ? propsWithDefaults.onMonthChange : noop;
  const appOnDayChange = (isDateTime || isDate) && propsWithDefaults.onDayChange ? propsWithDefaults.onDayChange : noop;
  const appOnYearChange =
    (isDateTime || isDate) && propsWithDefaults.onYearChange ? propsWithDefaults.onYearChange : noop;

  const emptyPickerStartingMonthDefault = `${new Date().getMonth() + 1}/__/${new Date().getFullYear()}`;
  const emptyPickerStartingMonth =
    (isDateTime || isDate) && propsWithDefaults.emptyPickerStartingMonth
      ? propsWithDefaults.emptyPickerStartingMonth
      : emptyPickerStartingMonthDefault;
  const onCalendarOpenFocusedDay =
    (isDateTime || isDate) && propsWithDefaults.onCalendarOpenFocusedDay
      ? propsWithDefaults.onCalendarOpenFocusedDay
      : undefined;
  /* -------------------------- -------------- --------------------------*/
  /* -------------------------- TIME OPTIONALS --------------------------*/
  /* -------------------------- -------------- --------------------------*/
  const getIsDisabledTime =
    (isDateTime || isTime) && propsWithDefaults.getIsDisabledTime
      ? propsWithDefaults.getIsDisabledTime
      : defaultReturnFalse;
  const appOnHourChange =
    (isDateTime || isTime) && propsWithDefaults.onHourChange ? propsWithDefaults.onHourChange : noop;
  const appOnMinuteChange =
    (isDateTime || isTime) && propsWithDefaults.onMinuteChange ? propsWithDefaults.onMinuteChange : noop;
  const appOnMeridiemChange =
    (isDateTime || isTime) && propsWithDefaults.onMeridiemChange ? propsWithDefaults.onMeridiemChange : noop;
  const minutesInterval =
    (isDateTime || isTime) && propsWithDefaults.minutesInterval ? propsWithDefaults.minutesInterval : 1;
  const onTimeWheelOpenStartingTime =
    (isDateTime || isTime) && propsWithDefaults.onTimeWheelOpenStartingTime
      ? propsWithDefaults.onTimeWheelOpenStartingTime
      : '01:00 AM';

  /* --------- ------------------------------------ ---------------------*/
  /* --------- SPECIFIC REQUIRED DATETIME OPTIONALS ---------------------*/
  /* --------- ------------------------------------ ---------------------*/
  const appOnDateChange =
    (isDateTime || isDate) && propsWithDefaults.onDateChange ? propsWithDefaults.onDateChange : noop;
  const appOnTimeChange =
    (isDateTime || isTime) && propsWithDefaults.onTimeChange ? propsWithDefaults.onTimeChange : noop;
  /* ---------------------- ---------------------- ----------------------*/
  /* ---------------------- DATE-RELATED OPTIONALS ----------------------*/
  /* ---------------------- ---------------------- ----------------------*/
  const getIsStartRangeDay =
    (isDateTime || isDate) && propsWithDefaults.getIsStartRangeDay ? propsWithDefaults.getIsStartRangeDay : noop;
  const getIsDayInRange =
    (isDateTime || isDate) && propsWithDefaults.getIsDayInRange ? propsWithDefaults.getIsDayInRange : noop;
  const getIsEndRangeDay =
    (isDateTime || isDate) && propsWithDefaults.getIsEndRangeDay ? propsWithDefaults.getIsEndRangeDay : noop;
  const fromDate = (isDateTime || isDate) && propsWithDefaults.fromDate;
  const toDate = (isDateTime || isDate) && propsWithDefaults.toDate;

  /* --------------------- ----------------------- ------------------------*/
  /* --------------------- TYPE SPECIFIC REQUIREDS ------------------------*/
  /* --------------------- ----------------------- ------------------------*/
  const appDate = isDate ? propsWithDefaults.date : '';
  const appTime = isTime ? propsWithDefaults.time : '';
  const appDateTime = isDateTime ? propsWithDefaults.dateTime : '';
  const appOnDateTimeChange = isDateTime ? propsWithDefaults.onDateTimeChange : noop;

  return useMemo(
    () => ({
      getIsDisabledDay,
      getIsOutOfRangeDay,
      getIsStartRangeDay,
      getIsDayInRange,
      getIsEndRangeDay,
      appOnDateChange,
      appOnMonthChange,
      appOnDayChange,
      appOnYearChange,
      appDate,
      getIsDisabledTime,
      appOnTimeChange,
      appOnHourChange,
      appOnMinuteChange,
      appOnMeridiemChange,
      appTime,
      appDateTime,
      appOnDateTimeChange,
      emptyPickerStartingMonth,
      onCalendarOpenFocusedDay,
      minutesInterval,
      onTimeWheelOpenStartingTime,
      isDate,
      isTime,
      isDateTime,
      fromDate,
      toDate,
    }),
    [
      getIsDisabledDay,
      getIsOutOfRangeDay,
      getIsStartRangeDay,
      getIsDayInRange,
      getIsEndRangeDay,
      appOnDateChange,
      appOnMonthChange,
      appOnDayChange,
      appOnYearChange,
      appDate,
      getIsDisabledTime,
      appOnTimeChange,
      appOnHourChange,
      appOnMinuteChange,
      appOnMeridiemChange,
      appTime,
      appDateTime,
      appOnDateTimeChange,
      emptyPickerStartingMonth,
      onCalendarOpenFocusedDay,
      minutesInterval,
      onTimeWheelOpenStartingTime,
      isDate,
      isTime,
      isDateTime,
      fromDate,
      toDate,
    ],
  );
};
